import { makeAutoObservable, reaction, runInAction } from 'mobx'

import { userManager } from '@/Core/Managers'
import { serverMessage } from '@/Ship/Helpers/serverError'
import { counterManager } from '@/Ship/Stores/CounterManager'
import { CheckGlobalResourceAccessTask } from '@/Ship/Tasks/CheckGlobalResourceAccessTask'
import { LoadActivityDashboardTask } from '@/Ship/Tasks/Dashboard/LoadActivityDashboardTask'
import { LoadDashboardEmployeesTask } from '@/Ship/Tasks/Dashboard/LoadDashboardEmployeesTask'
import { LoadHomeOverviewTask } from '@/Ship/Tasks/Dashboard/LoadHomeOverviewTask'
import { LoadTransportDashboardTask } from '@/Ship/Tasks/Dashboard/LoadTransportDashboardTask'

import type { IActivityDashboard, IDashboardEmployee } from '@/Ship/Contracts/IActivityDashboard'
import type { IHomeOverview, OverviewPeriod } from '@/Ship/Contracts/IHomeOverview'
import type { ITransportDashboard } from '@/Ship/Contracts/ITransportDashboard'

/** Ресурс і ключ права фільтра: `Activity.dashboard_others` з `/auth/permissions`. */
const ACTIVITY_RESOURCE = 'Crm.Activity'
const DASHBOARD_OTHERS = 'dashboard_others'

/** Права блоків перевезень: ключ `Report` з `/auth/permissions` (`SPEC-027` §6). */
const REPORT_RESOURCE = 'Report.Index'
const DASHBOARD_NO_OFFERS = 'dashboard_no_offers'
const DASHBOARD_DYNAMICS = 'dashboard_dynamics'

/** Пауза перед перечитуванням за подією: кілька подій поспіль — один запит. */
export const COUNTERS_REFRESH_DELAY_MS = 300

export const DASHBOARD_LOAD_ERROR = 'Не вдалося завантажити справи.'

export const TRANSPORT_LOAD_ERROR = 'Не вдалося завантажити віджети перевезень.'

export const OVERVIEW_LOAD_ERROR = 'Не вдалося завантажити показники.'

/** Період рейтингів за замовчуванням — той самий, що на беку (`HomeOverviewBounds::DEFAULT_PRESET`). */
export const DEFAULT_OVERVIEW_PERIOD: OverviewPeriod = 'quarter'

/** Значення верхньої опції фільтра «Усі співробітники» (уся компанія). */
export const ALL_EMPLOYEES = '__all__'

const currentUserUuid = (): string => String(userManager.user?.['uuid'] ?? '')

/**
 * Стан «Робочого столу» (`SPEC-015` §3.2, `SPEC-027` §3.5): обраний
 * співробітник — спільний для всіх віджетів сторінки, дані плиток справ і
 * віджетів перевезень, завантаження й помилка кожного з двох джерел окремо:
 * збій одного не гасить інше.
 *
 * Звідки числа:
 *  - відкриття сторінки й зміна фільтра — `GET /services/activities/dashboard`
 *    `GET /services/reports/transport_dashboard` (чотири блоки одним
 *    запитом, В-01) і `GET /services/reports/overview` (огляд: показники,
 *    тренд, рейси, рейтинги — теж одним запитом), паралельно — три запити на
 *    сторінку незалежно від кількості блоків;
 *  - подія `counters` (`counterManager.revision`, С-2) — перечитування, коли
 *    обрано себе: бейдж «Справи» оновлюється сам, і «Протерміновані справи»
 *    поруч не мають від нього відставати (ВП-2). Ті самі події несуть зміни
 *    заявок, запитів і тендерів, тож перечитуються й віджети перевезень. Справи
 *    чужого співробітника в канал користувача не приходять — його плитки
 *    оновлює відкриття й фільтр.
 *
 * Відповідь, що запізнилася, не перетирає свіжішу: фільтр змінили, поки
 * летів попередній запит, — цифри мусять бути того, кого обрано
 * (`generation`); старіша за вже показану відповідь того самого вибору теж
 * відкидається (`applied`). Але відповідь **того самого** вибору, що
 * прийшла після новішого запиту, застосовується: під потоком подій
 * `counters` кожне перечитування робило попереднє «застарілим», жодна
 * відповідь не доходила до екрана, і скелети висіли без кінця.
 *
 * Вибір між відвідуваннями не запам'ятовується (§5.2): `start()` скидає його.
 */
class DashboardManager {
    /** Обраний співробітник; `null` — свої цифри. */
    selectedUuid: string | null = null

    activity: IActivityDashboard | null = null

    /** Завантаження, яке видно (відкриття, фільтр); фонове за подією — ні. */
    isLoading = false

    error: string | null = null

    transport: ITransportDashboard | null = null

    isTransportLoading = false

    transportError: string | null = null

    /** Огляд: показники, тренд, воронка, рейси, рейтинги (`/services/reports/overview`). */
    overview: IHomeOverview | null = null

    isOverviewLoading = false

    overviewError: string | null = null

    /** Період рейтингів огляду; показники й тренд від нього не залежать. */
    period: OverviewPeriod = DEFAULT_OVERVIEW_PERIOD

    employees: IDashboardEmployee[] = []

    isEmployeesLoading = false

    /** Номер запиту: зростає з кожним читанням. */
    private requestId = 0

    /** Покоління вибору: міняється з відкриттям сторінки й фільтром. */
    private generation = 0

    /** Номер останньої застосованої відповіді кожного джерела. */
    private applied = { activity: 0, transport: 0, overview: 0 }

    private employeesController: AbortController | null = null

    constructor() {
        makeAutoObservable<this, 'requestId' | 'generation' | 'applied' | 'employeesController'>(this, {
            requestId: false,
            generation: false,
            applied: false,
            employeesController: false,
        })
    }

    /** Фільтр по співробітнику — за правом `dashboard_others` (A, SA). */
    get canFilterByEmployee(): boolean {
        return CheckGlobalResourceAccessTask(ACTIVITY_RESOURCE, DASHBOARD_OTHERS)
    }

    /**
     * Блок «Без пропозицій» — за правом глядача `dashboard_no_offers` (L, SL,
     * A); поки відповіді немає, право вирішує, чи малювати скелет. Бек віддає
     * `null` за тим самим правом — тоді блоку немає.
     */
    get showsNoOffers(): boolean {
        return this.transport
            ? this.transport.noOffers !== null
            : CheckGlobalResourceAccessTask(REPORT_RESOURCE, DASHBOARD_NO_OFFERS)
    }

    /** Блок «Динаміка» — так само, за правом `dashboard_dynamics`. */
    get showsDynamics(): boolean {
        return this.transport
            ? this.transport.dynamics !== null
            : CheckGlobalResourceAccessTask(REPORT_RESOURCE, DASHBOARD_DYNAMICS)
    }

    /** Обрано себе: подія `counters` стосується саме цих цифр. */
    get isOwn(): boolean {
        return this.selectedUuid === null || this.selectedUuid === currentUserUuid()
    }

    /**
     * Значення поля: обраний або поточний користувач — поле показує, чиї
     * цифри. Поки ПІБ невідомий (перша відповідь ще летить), поле порожнє, а
     * не з голим uuid.
     */
    get employeeValue(): string | undefined {
        // Нікого не обрано — «Усі співробітники» (уся компанія), а не власне ПІБ:
        // поле має казати правду про те, чиї цифри на екрані
        if (this.selectedUuid === null) return ALL_EMPLOYEES

        return this.employeeOptions.some((item) => item.uuid === this.selectedUuid) ? this.selectedUuid : undefined
    }

    /**
     * Варіанти селекта: знайдені плюс той, чиї цифри на екрані, — щоб поле
     * показувало ПІБ, а не uuid, ще до першого відкриття списку.
     */
    get employeeOptions(): IDashboardEmployee[] {
        const shown = this.activity?.user ?? this.transport?.user
        const options = [...this.employees]

        if (shown && shown.uuid !== '' && !options.some((item) => item.uuid === shown.uuid)) {
            options.unshift({ uuid: shown.uuid, fullName: shown.fullName ?? '' })
        }

        return options
    }

    /**
     * Відкриття сторінки: свої цифри й підписка на `counters`. Повертає
     * відписку для `useEffect`.
     */
    start(): () => void {
        this.generation++
        this.selectedUuid = null
        this.activity = null
        this.error = null
        this.transport = null
        this.transportError = null
        this.overview = null
        this.overviewError = null
        this.period = DEFAULT_OVERVIEW_PERIOD
        this.employees = []
        void this.load()

        return reaction(
            () => counterManager.revision,
            () => {
                if (this.isOwn) void this.load({ silent: true })
            },
            { delay: COUNTERS_REFRESH_DELAY_MS }
        )
    }

    /**
     * Зміна фільтра. Очищення (хрестик) і «Усі співробітники» — уся компанія;
     * обраний сам себе — саме свої цифри, а не компанія.
     */
    selectEmployee(uuid: string | null): void {
        const next = uuid === null || uuid === '' || uuid === ALL_EMPLOYEES ? null : uuid

        if (next === this.selectedUuid) return

        this.selectedUuid = next
        this.generation++
        void this.load()
    }

    /**
     * Огляд показує всю компанію, коли співробітника не обрано, — лише в
     * того, хто має фільтр (`dashboard_others`); решта бачить свої цифри.
     */
    get isCompanyScope(): boolean {
        return this.canFilterByEmployee && this.selectedUuid === null
    }

    /**
     * Чиї цифри в блоках заявок — для підзаголовків (рішення замовника
     * 2026-10-22): уся компанія (адміністратор без фільтра), обраний
     * співробітник або свої заявки — де ви менеджер клієнта чи логіст.
     */
    get applicationsScopeLabel(): string {
        if (this.isCompanyScope) return 'Уся компанія'
        if (this.isOwn) return 'Ваші заявки — менеджер клієнта або логіст'

        const name = this.employeeOptions.find((item) => item.uuid === this.selectedUuid)?.fullName

        return name ? `${name} — менеджер клієнта або логіст` : 'Обраний співробітник'
    }

    /** Те саме для плиток справ: справи, де співробітник відповідальний. */
    get activitiesScopeLabel(): string {
        if (this.isCompanyScope) return 'Уся компанія, незавершені'
        if (this.isOwn) return 'Ваші незавершені'

        return this.activity?.user?.fullName || 'Обраний співробітник'
    }

    /** Зміна періоду рейтингів — перечитується лише огляд. */
    setPeriod(period: OverviewPeriod): void {
        if (period === this.period) return

        this.period = period
        void this.loadOverview(++this.requestId, this.generation, this.selectedUuid, false)
    }

    /** Відповідь іншого вибору або старіша за вже показану — не застосовується. */
    private isStale(source: 'activity' | 'transport' | 'overview', id: number, generation: number): boolean {
        return generation !== this.generation || id < this.applied[source]
    }

    /** Плитки справ, віджети перевезень і огляд — паралельно, кожне зі своєю помилкою. */
    async load({ silent = false }: { silent?: boolean } = {}): Promise<void> {
        const id = ++this.requestId
        const generation = this.generation
        const uuid = this.selectedUuid

        await Promise.all([
            this.loadActivity(id, generation, uuid, silent),
            this.loadTransport(id, generation, uuid, silent),
            this.loadOverview(id, generation, uuid, silent),
        ])
    }

    private async loadOverview(id: number, generation: number, uuid: string | null, silent: boolean): Promise<void> {
        const period = this.period

        if (!silent) this.isOverviewLoading = true

        try {
            const data = await LoadHomeOverviewTask(uuid, period)

            // Відповідь іншого вибору чи періоду, що запізнилася, не перетирає свіжішу
            if (this.isStale('overview', id, generation) || period !== this.period) return

            runInAction(() => {
                this.applied.overview = id
                this.overview = data
                this.overviewError = null
                this.isOverviewLoading = false
            })
        } catch (error: unknown) {
            if (this.isStale('overview', id, generation) || period !== this.period) return

            runInAction(() => {
                this.applied.overview = id
                this.overview = null
                this.overviewError = serverMessage(error, OVERVIEW_LOAD_ERROR)
                this.isOverviewLoading = false
            })
        }
    }

    private async loadActivity(id: number, generation: number, uuid: string | null, silent: boolean): Promise<void> {
        if (!silent) this.isLoading = true

        try {
            const data = await LoadActivityDashboardTask(uuid)

            if (this.isStale('activity', id, generation)) return

            runInAction(() => {
                this.applied.activity = id
                this.activity = data
                this.error = null
                this.isLoading = false
            })
        } catch (error: unknown) {
            if (this.isStale('activity', id, generation)) return

            // Старі числа під новим вибором чи після збою — неправда: «—» і причина (Н-3)
            runInAction(() => {
                this.applied.activity = id
                this.activity = null
                this.error = serverMessage(error, DASHBOARD_LOAD_ERROR)
                this.isLoading = false
            })
        }
    }

    private async loadTransport(id: number, generation: number, uuid: string | null, silent: boolean): Promise<void> {
        if (!silent) this.isTransportLoading = true

        try {
            const data = await LoadTransportDashboardTask(uuid)

            if (this.isStale('transport', id, generation)) return

            runInAction(() => {
                this.applied.transport = id
                this.transport = data
                this.transportError = null
                this.isTransportLoading = false
            })
        } catch (error: unknown) {
            if (this.isStale('transport', id, generation)) return

            runInAction(() => {
                this.applied.transport = id
                this.transport = null
                this.transportError = serverMessage(error, TRANSPORT_LOAD_ERROR)
                this.isTransportLoading = false
            })
        }
    }

    /** Варіанти селекта: перша сторінка або пошук за ПІБ. */
    async searchEmployees(query: string): Promise<void> {
        this.employeesController?.abort()

        const controller = new AbortController()
        this.employeesController = controller
        this.isEmployeesLoading = true

        try {
            const items = await LoadDashboardEmployeesTask(query, controller.signal)

            if (controller.signal.aborted) return

            runInAction(() => {
                this.employees = items
            })
        } catch {
            // Список варіантів — допомога у виборі, а не дані: помилку покаже
            // порожній список, а плитки лишаються як є
        } finally {
            if (this.employeesController === controller) {
                runInAction(() => {
                    this.isEmployeesLoading = false
                })
            }
        }
    }
}

export const dashboardManager = new DashboardManager()
